import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import {
  Modal,
  Input,
  Button,
  Form,
  Tooltip,
  Space,
  Tag,
  message,
} from "antd";
import {
  CopyOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { sliceKey, reducer, actions } from "./slice";
import { shareReportSaga } from "./saga";
import * as selectors from "./selectors";

const { TextArea } = Input;

const ShareReport = ({ orderId }) => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: shareReportSaga });

  const dispatch = useDispatch();
  const loading = useSelector(selectors.selectLoading);
  const success = useSelector(selectors.selectSuccess);
  const error = useSelector(selectors.selectError);
  const [visible, setVisible] = useState(false);
  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [subject, setSubject] = useState(
    `FoodLabz Test Report - Order ${orderId}`
  );

  const verificationLink =
    `https://verify.foodlabz.com/report/${orderId}`;

  const emailPreview = `Subject: ${subject}

Hi,

Please find attached the test report for Order ${orderId}.

You can also verify this report online using our secure verification portal:
${verificationLink}

Best regards,
FoodLabz Team`;

  useEffect(() => {
    if (success) {
      message.success("Report sent successfully!");
      dispatch(actions.resetShareReportState());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error || "Failed to send report email");
      dispatch(actions.resetShareReportState());
    }
  }, [error, dispatch]);

  const handleCopy = () => {
    navigator.clipboard.writeText(verificationLink);
    message.success("Link copied to clipboard!");
  };

  const handleSend = () => {
    if (emails.length === 0) {
      message.warning("Please add at least one email address");
      return;
    }
    setVisible(false);
    setEmails([]);
    setInputValue("");
    dispatch(
      actions.sendReportEmail({
        orderId: orderId,
        emailIds: emails,
      })
    );
  };

  const handleInputConfirm = () => {
    if (inputValue && !emails.includes(inputValue)) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(inputValue)) {
        message.error("Please enter a valid email address");
        return;
      }
      setEmails([...emails, inputValue]);
    }
    setInputValue("");
  };

  const handleRemove = (removedEmail) => {
    setEmails(emails.filter((email) => email !== removedEmail));
  };

  return (
    <>
      <Button
        size="large"
        icon={<ShareAltOutlined />}
        onClick={() => setVisible(true)}
      >
        Share Report
      </Button>

      <Modal
        title="Share Report"
        visible={visible}
        onCancel={() => {
          setVisible(false);
          setEmails([]);
          setInputValue("");
        }}
        footer={null}
        centered
      >
        <Form layout="vertical">
          <Form.Item label="Email Addresses">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "8px",
                padding: "4px 8px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
              }}
              onClick={() => document.getElementById("email-input")?.focus()}
            >
              {emails.map((email) => (
                <Tag
                  key={email}
                  closable
                  onClose={() => handleRemove(email)}
                  style={{
                    marginBottom: 4,
                    background: "#f0f5ff",
                    border: "1px solid #91d5ff",
                  }}
                >
                  {email}
                </Tag>
              ))}
              <Input
                id="email-input"
                size="small"
                style={{ border: "none", flex: 1, minWidth: 120 }}
                placeholder={
                  emails.length === 0
                    ? "Enter email address and press Enter"
                    : ""
                }
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onPressEnter={handleInputConfirm}
                onBlur={handleInputConfirm}
              />
            </div>
            <div style={{ fontSize: 12, color: "#999" }}>
              Enter multiple email addresses separated by pressing Enter
            </div>
          </Form.Item>

          {/* Public Verification Link
          <Form.Item
            label={
              <Space>
                Public Verification Link
                <Tooltip title="Copy link">
                  <CopyOutlined
                    onClick={handleCopy}
                    style={{ cursor: "pointer", color: "#1890ff" }}
                  />
                </Tooltip>
              </Space>
            }
          >
            <Input value={verificationLink} readOnly />
          </Form.Item> */}

          <Form.Item label="Subject">
            <Input
              value={subject}
              readOnly
            />
          </Form.Item>

          <Form.Item label="Email Preview">
            <TextArea rows={8} value={emailPreview} readOnly />
          </Form.Item>

          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Space>
              <Button
                onClick={() => {
                  setVisible(false);
                  setEmails([]);
                  setInputValue("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleSend}
                disabled={emails.length === 0}
              >
                Send Report
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ShareReport;